import { ChevronDown, ChevronRight, StickyNote } from "lucide-react";
import { FC, memo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { cn } from "~/lib/utils";
import { shortRelativeTime } from "~/utils/date";
import type { PageAnnotation } from "../types";

interface PageAnnotationsListProps {
  annotations: PageAnnotation[];
  onAnnotationClick: (annotation: PageAnnotation) => void;
  activeAnnotationId: string | null;
}

const PageAnnotationsListComponent: FC<PageAnnotationsListProps> = ({
  annotations,
  onAnnotationClick,
  activeAnnotationId,
}) => {
  const intl = useIntl();
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (annotations.length === 0) {
    return null;
  }

  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setIsCollapsed((prev) => !prev)}
        className="w-full px-4 py-2 flex items-center gap-2 hover:bg-accent/50 transition-colors"
      >
        {isCollapsed ? (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <StickyNote className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <FormattedMessage
            id="pageAnnotations.title"
            defaultMessage="Page Notes"
          />
          <span className="ml-1 text-muted-foreground">
            ({annotations.length})
          </span>
        </span>
      </button>
      {!isCollapsed && (
        <div className="max-h-48 overflow-y-auto">
          {annotations.map((annotation) => {
            const timeAgo = shortRelativeTime(
              annotation.updatedAt,
              intl.locale
            );
            const authorName = annotation.authorId.split("@")[0];
            const previewText =
              annotation.content.length > 80
                ? `${annotation.content.slice(0, 80)}...`
                : annotation.content;

            const displayTitle = annotation.title || undefined;

            return (
              <button
                key={annotation.id}
                type="button"
                onClick={() => onAnnotationClick(annotation)}
                className={cn(
                  "w-full text-left px-4 py-2 hover:bg-accent/50 transition-colors flex items-start gap-2 border-l-2",
                  activeAnnotationId === annotation.id
                    ? "bg-accent/70"
                    : "border-l-transparent"
                )}
                style={{
                  borderLeftColor:
                    activeAnnotationId === annotation.id
                      ? annotation.color
                      : undefined,
                }}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 border"
                  style={{
                    backgroundColor: annotation.color,
                    borderColor: `${annotation.color}CC`,
                  }}
                />
                <div className="min-w-0 flex-1">
                  {displayTitle && (
                    <p className="text-xs font-medium text-foreground truncate">
                      {displayTitle}
                    </p>
                  )}
                  <p className="text-sm text-foreground truncate">
                    {previewText || (
                      <span className="text-muted-foreground italic">
                        <FormattedMessage
                          id="pageAnnotations.emptyNote"
                          defaultMessage="Empty note"
                        />
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {authorName} &middot; {timeAgo}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PageAnnotationsList = memo(PageAnnotationsListComponent);
PageAnnotationsList.displayName = "PageAnnotationsList";
